import * as XLSX from 'xlsx'

const parseCsvSegments = (
  text: string,
  isDelimiter: (char: string, nextChar: string | null, inQuotes: boolean) => { isDelimiter: boolean; skipChars: number }
): string[] => {
  const segments: string[] = []
  let currentSegment = ''
  let inQuotes = false
  let i = 0

  while (i < text.length) {
    const char = text[i]
    const nextChar = i + 1 < text.length ? text[i + 1] : null

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote inside quoted field — preserve both characters
        // so that row-splitting does not consume quote escaping that
        // column-splitting still needs.
        currentSegment += '""'
        i += 2
        continue
      } else {
        // Toggle quote state
        inQuotes = !inQuotes
        currentSegment += char
        i++
        continue
      }
    }

    // Check for delimiter (only when not in quotes)
    if (!inQuotes) {
      const delimiterCheck = isDelimiter(char, nextChar, inQuotes)
      if (delimiterCheck.isDelimiter) {
        segments.push(currentSegment)
        currentSegment = ''
        i += delimiterCheck.skipChars
        continue
      }
    }

    currentSegment += char
    i++
  }

  // Push the last segment if there's any content
  if (currentSegment.length > 0 || segments.length === 0) {
    segments.push(currentSegment)
  }

  return segments
}

const parseCsvRows = (csvText: string): string[] => {
  return parseCsvSegments(csvText, (char, nextChar) => {
    if (char === '\r' && nextChar === '\n') {
      return { isDelimiter: true, skipChars: 2 } // Windows line ending \r\n
    } else if (char === '\n') {
      return { isDelimiter: true, skipChars: 1 } // Unix line ending \n
    } else if (char === '\r') {
      return { isDelimiter: true, skipChars: 1 } // Old Mac line ending \r
    }
    return { isDelimiter: false, skipChars: 0 }
  })
}

const parseCsvColumns = (rowText: string): string[] => {
  return parseCsvSegments(rowText, (char) => {
    if (char === ',') {
      return { isDelimiter: true, skipChars: 1 }
    }
    return { isDelimiter: false, skipChars: 0 }
  })
}

const EXTENSIONS_XLS = ['xlsx', 'xls']

const readExcelAsCsv = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        if (!e.target?.result) return reject(new Error('Failed to read file'))
        const data = new Uint8Array(e.target.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array', cellDates: false })
        const firstSheetName = workbook.SheetNames[0]
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName], {
          rawNumbers: true
        })
        resolve(csv)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

const isExcelTimestamp = (value: string): boolean => {
  const num = parseFloat(value)
  // Excel dates are typically between 1 (1900-01-01) and ~50000 (year 2036)
  // This range covers reasonable dates
  return !isNaN(num) && num > 0 && num < 100000 && value === num.toString()
}

const convertExcelTimestampToISODate = (excelTimestamp: number): string => {
  const jsDate = new Date((excelTimestamp - 25569) * 86400 * 1000)
  const year = jsDate.getUTCFullYear()
  const month = String(jsDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(jsDate.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const convertExcelTimestampToISODatetime = (excelTimestamp: number): string => {
  const jsDate = new Date((excelTimestamp - 25569) * 86400 * 1000)
  const year = jsDate.getUTCFullYear()
  const month = String(jsDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(jsDate.getUTCDate()).padStart(2, '0')
  const hours = String(jsDate.getUTCHours()).padStart(2, '0')
  const minutes = String(jsDate.getUTCMinutes()).padStart(2, '0')
  const seconds = String(jsDate.getUTCSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const CSV_PREVIEW_BYTES = 2 * 1024 * 1024 // 2 MB — enough for thousands of preview rows

/**
 * Read only the beginning of a CSV file for preview purposes.
 * Returns the text (trimmed to the last complete line) and whether the file was truncated.
 */
const readCsvPreview = async (
  file: File,
  maxBytes: number = CSV_PREVIEW_BYTES
): Promise<{ text: string; truncated: boolean }> => {
  if (file.size <= maxBytes) {
    return { text: await file.text(), truncated: false }
  }
  const raw = await file.slice(0, maxBytes).text()
  // Find the last newline that is outside a quoted field so we don't
  // truncate mid-field (CSV allows literal newlines inside quotes).
  let lastSafe = -1
  let inQuotes = false
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]
    if (ch === '"') {
      if (inQuotes && i + 1 < raw.length && raw[i + 1] === '"') {
        i++ // skip escaped quote
      } else {
        inQuotes = !inQuotes
      }
    } else if (!inQuotes) {
      if (ch === '\r') {
        if (i + 1 < raw.length && raw[i + 1] === '\n') {
          lastSafe = i + 1
          i++
        } else {
          lastSafe = i
        }
      } else if (ch === '\n') {
        lastSafe = i
      }
    }
  }
  return { text: lastSafe > 0 ? raw.slice(0, lastSafe) : raw, truncated: true }
}

/**
 * Async generator that streams a CSV file in chunks, yielding complete
 * row segments (text containing one or more full CSV rows).
 * Handles quoted fields that may span chunk boundaries.
 */
async function* streamCsvChunks(file: File, chunkSize: number = 5 * 1024 * 1024): AsyncGenerator<string> {
  let offset = 0
  let carry = ''

  while (offset < file.size) {
    const end = Math.min(offset + chunkSize, file.size)
    const chunkText = await file.slice(offset, end).text()
    const text = carry + chunkText
    offset = end

    if (offset >= file.size) {
      // Last chunk — yield everything remaining
      if (text) yield text
      break
    }

    // Find the last newline that is outside a quoted field
    let lastSafe = -1
    let inQuotes = false
    for (let i = 0; i < text.length; i++) {
      const ch = text[i]
      if (ch === '"') {
        if (inQuotes && i + 1 < text.length && text[i + 1] === '"') {
          i++ // skip escaped quote
        } else {
          inQuotes = !inQuotes
        }
      } else if (!inQuotes) {
        if (ch === '\r') {
          if (i + 1 < text.length && text[i + 1] === '\n') {
            lastSafe = i + 1
            i++
          } else {
            lastSafe = i
          }
        } else if (ch === '\n') {
          lastSafe = i
        }
      }
    }

    if (lastSafe === -1) {
      // No safe split point — carry entire text to next chunk
      carry = text
    } else {
      yield text.slice(0, lastSafe + 1)
      carry = text.slice(lastSafe + 1)
    }
  }
}

export {
  EXTENSIONS_XLS,
  readExcelAsCsv,
  isExcelTimestamp,
  convertExcelTimestampToISODate,
  convertExcelTimestampToISODatetime,
  parseCsvColumns,
  parseCsvRows,
  readCsvPreview,
  streamCsvChunks
}
